import {
  Button,
  Content,
  Header,
  HeaderContainer,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderName,
  HeaderPanel,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  SkipToContent,
  Switcher,
  SwitcherDivider,
  SwitcherItem,
} from '@carbon/react';
import { Help, Logout, Translate, UserAvatar, type CarbonIconType } from '@carbon/icons-react';
import type { SessionDto } from '@pos/api-client';
import { PoweredBy } from '@pos/design-system';
import { LANGUAGES, useTranslation, type I18nKey, type Language } from '@pos/i18n';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useProductName } from './components/useProductName';
import { hasPermission } from './session';
import './AdminShell.scss';

export interface NavLinkItem {
  label: I18nKey;
  path: string;
  /** Hidden unless the user has it. */
  permission?: string;
  icon?: CarbonIconType;
}

/** A collapsible group in the side nav; hidden when none of its links are visible. */
export interface NavGroup {
  label: I18nKey;
  icon?: CarbonIconType;
  items: NavLinkItem[];
}

export type NavItem = NavLinkItem | NavGroup;

type PanelName = 'language' | 'account';

/** Carbon's lg breakpoint: from here the side nav stays open; below it, it opens over the content. */
const PERSISTENT_NAV_QUERY = '(min-width: 66rem)';

const isGroup = (item: NavItem): item is NavGroup => 'items' in item;

/**
 * Carbon UI shell for the admin portals (DESIGN_SYSTEM.md §13): header with the menu button, product name and global
 * actions; the left side nav; and the right-side header panels for language and the account. One panel is open at a
 * time; Escape or a click outside closes it.
 */
export function AdminShell({
  productName,
  title,
  context,
  nav,
  session,
  onLogOff,
  language,
  onLanguageChange,
  helpHref,
  accountLinks,
  notice,
  children,
}: {
  /** Overrides the product name from branding.json (packages/config); leave out to use branding. */
  productName?: string;
  /** The portal's name, already translated. */
  title: string;
  /** Where the user is working, already translated: e.g. "Tenant DEV" or "Store DEV". */
  context?: string;
  nav: NavItem[];
  session: SessionDto;
  onLogOff(): void;
  language: Language;
  onLanguageChange(language: Language): void;
  /** Shows a "User guide" header action that opens this address in the same tab (the HO portal links /docs). */
  helpHref?: string;
  /** Pages of the user's own account in the account panel, e.g. two-step sign-in at HO (ADR 0121). */
  accountLinks?: readonly { label: string; path: string }[];
  /** Shown above every page, e.g. that some permissions wait for two-step sign-in. */
  notice?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const brandedName = useProductName();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<PanelName>();
  const actionsRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!panel) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPanel(undefined);
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panelRef.current?.contains(target) && !actionsRef.current?.contains(target)) setPanel(undefined);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [panel]);

  const toggle = (name: PanelName) => setPanel((open) => (open === name ? undefined : name));
  const visibleNav = nav
    .map((item) =>
      isGroup(item) ? { ...item, items: item.items.filter((link) => hasPermission(session, link.permission)) } : item,
    )
    .filter((item) => (isGroup(item) ? item.items.length > 0 : hasPermission(session, item.permission)));

  return (
    <HeaderContainer
      render={({ isSideNavExpanded, onClickSideNavExpand }) => (
        <>
          <Header aria-label={title}>
            <SkipToContent>{t('carbon.skipToContent')}</SkipToContent>
            <HeaderMenuButton
              aria-label={isSideNavExpanded ? t('carbon.closeMenu') : t('carbon.openMenu')}
              aria-expanded={isSideNavExpanded}
              isActive={isSideNavExpanded}
              onClick={onClickSideNavExpand}
            />
            <HeaderName as={Link} to="/" prefix={productName ?? brandedName}>
              {title}
            </HeaderName>
            <HeaderGlobalBar>
              <div ref={actionsRef} className="pos-admin-shell__actions">
                {helpHref && (
                  <HeaderGlobalAction
                    aria-label={t('docs.userGuide')}
                    tooltipAlignment="end"
                    onClick={() => void navigate(helpHref)}
                  >
                    <Help size={20} />
                  </HeaderGlobalAction>
                )}
                <HeaderGlobalAction
                  aria-label={t('common.language')}
                  aria-expanded={panel === 'language'}
                  isActive={panel === 'language'}
                  tooltipAlignment="end"
                  onClick={() => toggle('language')}
                >
                  <Translate size={20} />
                </HeaderGlobalAction>
                <HeaderGlobalAction
                  aria-label={t('admin.account.title')}
                  aria-expanded={panel === 'account'}
                  isActive={panel === 'account'}
                  tooltipAlignment="end"
                  onClick={() => toggle('account')}
                >
                  <UserAvatar size={20} />
                </HeaderGlobalAction>
              </div>
            </HeaderGlobalBar>

            <HeaderPanel
              ref={panelRef}
              expanded={Boolean(panel)}
              aria-label={panel ? t(PANEL_LABELS[panel]) : undefined}
            >
              {panel === 'language' && (
                <Switcher aria-label={t('common.language')}>
                  {LANGUAGES.map((option) => (
                    <SwitcherItem
                      key={option.code}
                      aria-label={option.label}
                      isSelected={option.code === language}
                      onClick={() => {
                        setPanel(undefined);
                        onLanguageChange(option.code);
                      }}
                    >
                      {option.label}
                    </SwitcherItem>
                  ))}
                </Switcher>
              )}
              {panel === 'account' && (
                <section className="pos-admin-shell__account" aria-label={t('admin.account.title')}>
                  <p className="pos-admin-shell__account-name">{session.staff.displayName}</p>
                  <p className="pos-admin-shell__account-meta">
                    {t('admin.account.staffCode', { code: session.staff.staffCode })}
                  </p>
                  {context && <p className="pos-admin-shell__account-meta">{context}</p>}
                  <SwitcherDivider />
                  {accountLinks?.map((link) => (
                    <Button
                      key={link.path}
                      kind="ghost"
                      onClick={() => {
                        setPanel(undefined);
                        void navigate(link.path);
                      }}
                    >
                      {link.label}
                    </Button>
                  ))}
                  <Button kind="ghost" renderIcon={Logout} onClick={onLogOff}>
                    {t('login.logOff')}
                  </Button>
                </section>
              )}
            </HeaderPanel>

            <AdminSideNav label={title} nav={visibleNav} expanded={isSideNavExpanded} onToggle={onClickSideNavExpand} />
          </Header>
          <Content id="main-content" className="pos-admin-shell__content">
            {notice}
            {children}
            <footer className="pos-admin-shell__footer">
              <PoweredBy />
            </footer>
          </Content>
        </>
      )}
    />
  );
}

const PANEL_LABELS: Record<PanelName, I18nKey> = { language: 'common.language', account: 'admin.account.title' };

function AdminSideNav({
  label,
  nav,
  expanded,
  onToggle,
}: {
  label: string;
  nav: NavItem[];
  expanded: boolean;
  onToggle(): void;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const isActive = (path: string) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  // Below the lg breakpoint the nav opens over the content; close it once the user has picked a page.
  useEffect(() => {
    if (expanded && !window.matchMedia(PERSISTENT_NAV_QUERY).matches) onToggle();
    // Only on navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <SideNav aria-label={label} expanded={expanded} onOverlayClick={onToggle} onSideNavBlur={onToggle}>
      <SideNavItems>
        {nav.map((item) =>
          isGroup(item) ? (
            <SideNavMenu
              key={item.label}
              title={t(item.label)}
              renderIcon={item.icon}
              defaultExpanded={item.items.some((link) => isActive(link.path))}
              isActive={item.items.some((link) => isActive(link.path))}
            >
              {item.items.map((link) => (
                <SideNavMenuItem key={link.path} as={Link} to={link.path} isActive={isActive(link.path)}>
                  {t(link.label)}
                </SideNavMenuItem>
              ))}
            </SideNavMenu>
          ) : (
            <SideNavLink key={item.path} as={Link} to={item.path} renderIcon={item.icon} isActive={isActive(item.path)}>
              {t(item.label)}
            </SideNavLink>
          ),
        )}
      </SideNavItems>
    </SideNav>
  );
}
